import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { expenseAPI, budgetAPI } from '../services/api'
import { formatCurrency, formatDate, getCategoryIcon, getCategoryLabel, getMonthYear } from '../utils/helpers'
import BudgetSetting from '../components/BudgetSetting'

export default function Home() {
  const [summary, setSummary] = useState(null)
  const [recentExpenses, setRecentExpenses] = useState([])
  const [budget, setBudget] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showBudgetModal, setShowBudgetModal] = useState(false)

  const currentMonth = getMonthYear()

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    setLoading(true)
    try {
      const [summaryData, expensesData, budgetData] = await Promise.all([
        expenseAPI.getSummary({ period: 'month' }),
        expenseAPI.getAll({ limit: 5, sort: 'newest' }),
        budgetAPI.get(currentMonth),
      ])
      setSummary(summaryData)
      setRecentExpenses(expensesData.expenses || [])
      setBudget(budgetData)
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const budgetPercentage = budget?.amount
    ? Math.min((summary?.total / budget.amount) * 100, 100)
    : 0

  const isAlmostOver = budgetPercentage >= 80 && budgetPercentage < 100
  const isOver = budgetPercentage >= 100

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map(i => (
          <div key={i} className="card animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-1/3 mb-3" />
            <div className="h-8 bg-gray-200 rounded w-2/3" />
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Greeting */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Halo! 👋</h2>
        <p className="text-gray-500 text-sm">Ringkasan keuanganmu hari ini</p>
      </div>

      {/* Budget Alert */}
      {isOver && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
          <span className="text-2xl">🚨</span>
          <div>
            <p className="font-semibold text-red-700">Budget Bulan Ini Habis!</p>
            <p className="text-red-600 text-sm">Kamu sudah melebihi budget yang ditetapkan.</p>
          </div>
        </div>
      )}
      
      {isAlmostOver && !isOver && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex items-center gap-3">
          <span className="text-2xl">⚠️</span>
          <div>
            <p className="font-semibold text-yellow-700">Budget Hampir Habis!</p>
            <p className="text-yellow-600 text-sm">Pengeluaranmu sudah mencapai {budgetPercentage.toFixed(0)}% dari budget.</p>
          </div>
        </div>
      )}

      {/* Summary Card */}
      <div className="bg-gradient-to-br from-blue-600 to-blue-800 rounded-2xl p-6 text-white">
        <p className="text-blue-200 text-sm mb-1">Total Pengeluaran Bulan Ini</p>
        <p className="text-3xl font-bold mb-4">
          {formatCurrency(summary?.total || 0)}
        </p>

        {/* Budget Progress */}
        {budget?.amount ? (
          <div>
            <div className="flex justify-between text-sm mb-2">
              <span className="text-blue-200">Budget</span>
              <span className="font-medium">{formatCurrency(budget.amount)}</span>
            </div>
            <div className="w-full bg-blue-900/50 rounded-full h-2.5">
              <div
                className={`h-2.5 rounded-full transition-all ${
                  isOver ? 'bg-red-400' : isAlmostOver ? 'bg-yellow-400' : 'bg-green-400'
                }`}
                style={{ width: `${budgetPercentage}%` }}
              />
            </div>
            <div className="flex justify-between text-xs mt-1 text-blue-200">
              <span>{budgetPercentage.toFixed(0)}% terpakai</span>
              <span>Sisa {formatCurrency(Math.max(budget.amount - (summary?.total || 0), 0))}</span>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setShowBudgetModal(true)}
            className="text-blue-200 text-sm underline hover:text-white"
          >
            + Set budget bulan ini
          </button>
        )}
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 gap-4">
        <div className="card">
          <p className="text-gray-500 text-xs mb-1">Hari Ini</p>
          <p className="text-xl font-bold text-gray-900">{formatCurrency(summary?.today || 0)}</p>
        </div>
        <div className="card">
          <p className="text-gray-500 text-xs mb-1">Rata-rata/Hari</p>
          <p className="text-xl font-bold text-gray-900">{formatCurrency(summary?.average || 0)}</p>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 gap-4">
        <Link 
          to="/expenses"
          className="card flex items-center gap-3 hover:shadow-lg transition-shadow cursor-pointer"
        >
          <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
            <span className="text-xl">💸</span>
          </div>
          <div>
            <p className="font-semibold text-gray-900">Pengeluaran</p>
            <p className="text-xs text-gray-500">Lihat semua</p>
          </div>
        </Link>
        <Link 
          to="/wishlist"
          className="card flex items-center gap-3 hover:shadow-lg transition-shadow cursor-pointer"
        >
          <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
            <span className="text-xl">🎁</span>
          </div>
          <div>
            <p className="font-semibold text-gray-900">Wishlist</p>
            <p className="text-xs text-gray-500">Lihat semua</p>
          </div>
        </Link>
      </div>

      {/* Recent Expenses */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-bold text-gray-900">Transaksi Terakhir</h3>
          <Link to="/expenses" className="text-primary text-sm hover:underline">Lihat semua</Link>
        </div>

        {recentExpenses.length === 0 ? (
          <div className="card text-center py-8">
            <p className="text-4xl mb-2">📭</p>
            <p className="text-gray-500">Belum ada transaksi</p>
          </div>
        ) : (
          <div className="space-y-3">
            {recentExpenses.map((expense) => (
              <div key={expense.id} className="card flex items-center gap-4 py-3">
                <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center text-xl flex-shrink-0">
                  {getCategoryIcon(expense.category)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">
                    {expense.description || getCategoryLabel(expense.category)}
                  </p>
                  <p className="text-xs text-gray-500">{formatDate(expense.date)}</p>
                </div>
                <p className="font-semibold text-gray-900 flex-shrink-0">
                  {formatCurrency(expense.amount)}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Budget Setting Modal */}
      {showBudgetModal && (
        <BudgetSetting
          currentBudget={budget}
          onClose={() => setShowBudgetModal(false)}
          onSuccess={() => {
            setShowBudgetModal(false)
            fetchData()
          }}
        />
      )}
    </div>
  )
}
